/**
 * DeviceDetail page: per-device controls + live command history.
 *
 * Cameras list, mode/disk readout, bitrate slider (debounced settings.update),
 * codec toggle, recent commands. Drives the same enqueueFleetCommand path
 * the Fleet page uses.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Play,
  Square,
  Circle as RecordCircle,
  RefreshCw,
  RotateCcw,
  Share2,
  HardDrive,
  MemoryStick,
  Wifi,
  WifiOff,
  Camera,
  SignalLow,
  SignalHigh,
  X as XIcon,
  Pencil,
  Check,
  Trash2,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import DeleteSessionDialog from './DeleteSessionDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatBytes, formatRelative } from '@/lib/utils';
import HelpTip from './HelpTip';
import SpeedTestCard from './SpeedTestCard';
import api from '../services/api';
import { useCurrentEvent } from '../state/currentEvent';
import {
  DeviceSession,
  FleetArchiveRequest,
  FleetCamera,
  FleetCommand,
  FleetDevice,
  FleetEvent,
  FleetIncident,
} from '../types';
import SnapshotDialog from './SnapshotDialog';
import ShareModal from './ShareModal';
import ConnectionPanel from './ConnectionPanel';
import RestartConfirmDialog from './RestartConfirmDialog';
import UploadTelemetryPanel from './UploadTelemetryPanel';

const STATUS_BADGE_CLASS: Record<string, string> = {
  queued: 'border-fg-muted text-fg-muted',
  sending: 'border-status-warning text-status-warning',
  received: 'border-status-buffered text-status-buffered',
  in_progress: 'border-status-buffered text-status-buffered',
  done: 'border-status-online text-status-online',
  failed: 'border-status-error text-status-error',
  superseded: 'border-fg-muted text-fg-muted',
  cancelled: 'border-fg-muted text-fg-muted',
  timed_out: 'border-status-error text-status-error',
};

interface DeviceDetailProps {
  device: FleetDevice;
  onBack: () => void;
  onCommand: (kind: string, payload?: Record<string, any>) => Promise<void>;
  onRefresh: () => void;
}

// ── Phone capture state (Phase 2.F.1) ─────────────────────────────────────
//
// V2 phone exposes its actual capture state via the WS snapshot
// (gps_rate_hz, imu_rate_hz, thermal_state, fgs_running,
// active_rally_event, upload_backlog_*_mb) — this card surfaces those
// as read-only chips. No mutable controls yet: raise_bitrate /
// lower_bitrate / force_resync are accepted on the wire (Phase 2.B.1)
// but the phone-side handlers don't exist yet (Phase 2.M.*), so the
// buttons stay out of the operator UI until they actually do
// something. Live state is still valuable on its own.

const THERMAL_BORDER: Record<string, string> = {
  ok: 'border-status-online text-status-online',
  warm: 'border-status-warning text-status-warning',
  hot: 'border-status-error text-status-error',
};

interface PhoneSettingsProps {
  device: FleetDevice;
}

const PhoneCaptureSettings: React.FC<PhoneSettingsProps> = ({ device }) => {
  return (
    <Card className="border-border bg-bg-surface">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-1.5">
          <h3 className="text-base font-medium text-accent-purple">Phone capture state</h3>
          <HelpTip>Live state from the phone's WS heartbeat.</HelpTip>
        </div>
        {(device.thermal_state === 'hot' || device.thermal_state === 'warm') && (
          <div
            className={cn(
              'mb-3 rounded-md border px-3 py-2 text-sm font-medium',
              device.thermal_state === 'hot'
                ? 'border-status-error bg-status-error/10 text-status-error'
                : 'border-status-warning bg-status-warning/10 text-status-warning',
            )}
          >
            {device.thermal_state === 'hot'
              ? 'Phone hot — shade it now. Stream may drop and video may pause to cool down; GPS/telemetry keep recording.'
              : 'Phone warming — get it out of direct sun before it throttles.'}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {device.gps_rate_hz != null && (
            <Badge variant="outline" className="border-status-buffered font-mono text-status-buffered">
              gps {device.gps_rate_hz.toFixed(1)} Hz
            </Badge>
          )}
          {device.imu_rate_hz != null && (
            <Badge variant="outline" className="border-status-buffered font-mono text-status-buffered">
              imu {device.imu_rate_hz.toFixed(1)} Hz
            </Badge>
          )}
          {/* M4 — parc-fermé spot check: which config revision the
              phone holds + which stage its evaluator thinks is live.
              Compare rev against the Stages tab's RevisionBar. */}
          {device.reported_geofence_revision != null && (
            <Badge
              variant="outline"
              className="border-status-buffered font-mono text-status-buffered"
              title="Geofence config revision this device last reported — compare against the event's revision in the Stages tab"
            >
              cfg r{device.reported_geofence_revision}
            </Badge>
          )}
          {device.reported_stage_index != null && (
            <Badge
              variant="outline"
              className="border-status-online font-mono text-status-online"
              title="Stage this device's evaluator reports as actively recording"
            >
              SS{device.reported_stage_index + 1} live
            </Badge>
          )}
          {device.thermal_state && (
            <Badge
              variant="outline"
              className={cn(
                'font-mono',
                THERMAL_BORDER[device.thermal_state] || 'border-fg-muted text-fg-muted',
              )}
            >
              thermal: {device.thermal_state}
            </Badge>
          )}
          {device.fgs_running != null && (
            <Badge
              variant="outline"
              className={cn(
                'font-mono',
                device.fgs_running
                  ? 'border-status-online text-status-online'
                  : 'border-fg-muted text-fg-muted',
              )}
            >
              {device.fgs_running ? 'fgs running' : 'fgs stopped'}
            </Badge>
          )}
          {device.active_rally_event && (
            <Badge variant="outline" className="border-accent-purple font-mono text-accent-purple">
              event: {device.active_rally_event}
            </Badge>
          )}
          {device.upload_backlog_stream_mb != null && (
            <Badge variant="outline" className="font-mono text-fg">
              low quality backlog {device.upload_backlog_stream_mb.toFixed(1)} MB
            </Badge>
          )}
          {device.upload_backlog_archive_mb != null && (
            <Badge variant="outline" className="font-mono text-fg">
              high quality backlog {device.upload_backlog_archive_mb.toFixed(1)} MB
            </Badge>
          )}
          {device.gps_rate_hz == null &&
            device.imu_rate_hz == null &&
            device.thermal_state == null &&
            device.fgs_running == null && (
              <span className="text-xs text-fg-muted">
                no V2 snapshot fields yet (phone may need a fresh build)
              </span>
            )}
        </div>
      </CardContent>
    </Card>
  );
};

interface CodecPreferenceProps {
  device: FleetDevice;
  onRefresh: () => void;
}

/**
 * Phase 1 (transcode-rollout) operator-controlled per-device encoder
 * preference toggle. Off (NULL) = phone uses its hardcoded default
 * ([AVC, HEVC]); on = flip to HEVC primary ([HEVC, AVC]).
 *
 * The backend writes FleetDevice.codec_preference (source of truth)
 * and enqueues a set_codec_preference fleet command in the same
 * request. Phone-side CodecPreferenceStore picks the override up on
 * the next recording session.
 */
const CodecPreferenceCard: React.FC<CodecPreferenceProps> = ({ device, onRefresh }) => {
  const isHevcPrimary =
    Array.isArray(device.codec_preference) &&
    device.codec_preference[0] === 'video/hevc';
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async (enable: boolean) => {
    setPending(true);
    setError(null);
    try {
      await api.setDeviceCodecPreference(
        device.device_id,
        enable ? ['video/hevc', 'video/avc'] : null,
      );
      onRefresh();
    } catch (e) {
      const msg = (e as { message?: string })?.message ?? String(e);
      setError(msg);
    } finally {
      setPending(false);
    }
  };

  return (
    <Card className="border-border bg-bg-surface">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-1.5">
          <h3 className="text-base font-medium text-accent-purple">Encoder codec</h3>
          <HelpTip>
            Default is AVC. HEVC primary saves ~30% bandwidth on the
            low-quality tier; the phone falls back to AVC automatically if
            its hardware encoder can't honor HEVC. Applies on the next
            recording session.
          </HelpTip>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-sm">HEVC primary</span>
            <Badge variant="outline" className="w-fit font-mono text-xs">
              {isHevcPrimary
                ? device.codec_preference!.join(' → ')
                : 'default (video/avc → video/hevc)'}
            </Badge>
          </div>
          <Switch
            checked={isHevcPrimary}
            disabled={pending}
            onCheckedChange={handleToggle}
          />
        </div>
        {/* Always-visible warning (Kris 2026-07-03) — this is the one
            piece of copy that must NOT hide behind the help icon. */}
        <p
          className={cn(
            'mt-2 text-xs leading-snug',
            isHevcPrimary ? 'font-medium text-status-error' : 'text-status-warning',
          )}
        >
          ⚠ HEVC breaks the public player for every viewer whose browser
          can't decode HEVC. Not recommended unless you know what you're
          doing.
        </p>
        {error && (
          <p className="mt-2 text-xs text-status-error">Save failed: {error}</p>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * M3 — per-device quality override card. Sparse per-tier deltas
 * (bitrate / resolution / fps) that WIN over the global tier profiles
 * for this device only: test different quality levels, or let a more
 * capable phone run hotter. Applies on the next recording session via
 * a merged set_capture_settings command; the fleet row badges the
 * device while an override is active.
 */
const TIER_LABELS: Record<string, string> = {
  stream: 'Low tier (live stream)',
  archive: 'High tier (archive)',
};

const QualityOverrideCard: React.FC<CodecPreferenceProps> = ({ device, onRefresh }) => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Draft: string fields so partially-typed numbers don't fight the
  // input. Hydrated from the device row per tier on device change.
  const emptyTier = { width: '', height: '', bitrate_mbps: '', fps: '' };
  const [draft, setDraft] = useState<Record<string, typeof emptyTier>>({
    stream: { ...emptyTier },
    archive: { ...emptyTier },
  });
  useEffect(() => {
    const tiers = device.capture_override?.tiers ?? {};
    const hydrate = (tier: string) => ({
      width: tiers[tier]?.width != null ? String(tiers[tier]!.width) : '',
      height: tiers[tier]?.height != null ? String(tiers[tier]!.height) : '',
      bitrate_mbps:
        tiers[tier]?.bitrate_bps != null ? String(tiers[tier]!.bitrate_bps! / 1e6) : '',
      fps: tiers[tier]?.fps != null ? String(tiers[tier]!.fps) : '',
    });
    setDraft({ stream: hydrate('stream'), archive: hydrate('archive') });
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.device_id, JSON.stringify(device.capture_override ?? null)]);

  const hasOverride = device.capture_override?.tiers != null;

  const buildOverride = () => {
    const tiers: Record<string, Record<string, number>> = {};
    for (const tier of ['stream', 'archive']) {
      const d = draft[tier];
      const delta: Record<string, number> = {};
      if (d.width.trim()) delta.width = Number(d.width);
      if (d.height.trim()) delta.height = Number(d.height);
      if (d.bitrate_mbps.trim()) delta.bitrate_bps = Math.round(Number(d.bitrate_mbps) * 1e6);
      if (d.fps.trim()) delta.fps = Number(d.fps);
      for (const v of Object.values(delta)) {
        if (!Number.isFinite(v) || v <= 0) return null;
      }
      if (Object.keys(delta).length > 0) tiers[tier] = delta;
    }
    return Object.keys(tiers).length > 0 ? { tiers } : null;
  };

  const submit = async (override: ReturnType<typeof buildOverride>) => {
    setPending(true);
    setError(null);
    try {
      await api.setDeviceCaptureOverride(device.device_id, override);
      onRefresh();
    } catch (e) {
      setError((e as { message?: string })?.message ?? String(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <Card className="border-border bg-bg-surface md:col-span-2">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-1.5">
          <h3 className="text-base font-medium text-accent-purple">Quality override</h3>
          <HelpTip>
            Per-device deltas that win over the global tier profiles —
            leave a field empty to inherit the global value. Applies on
            the next recording session. Use to test quality levels or
            push a more capable phone harder.
          </HelpTip>
          {hasOverride && (
            <Badge
              variant="outline"
              className="border-status-warning/60 font-mono text-[10px] text-status-warning"
            >
              override active
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {(['stream', 'archive'] as const).map((tier) => (
            <div key={tier} className="rounded border border-border bg-bg-elevated p-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                {TIER_LABELS[tier]}
              </div>
              <div className="grid grid-cols-4 gap-2">
                {(
                  [
                    ['width', 'W px'],
                    ['height', 'H px'],
                    ['bitrate_mbps', 'Mbps'],
                    ['fps', 'fps'],
                  ] as const
                ).map(([field, label]) => (
                  <label key={field} className="flex flex-col gap-1">
                    <span className="text-[9px] font-semibold uppercase tracking-wide text-fg-muted">
                      {label}
                    </span>
                    <Input
                      value={draft[tier][field]}
                      placeholder="—"
                      inputMode="decimal"
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          [tier]: { ...prev[tier], [field]: e.target.value },
                        }))
                      }
                      className="h-8 font-mono text-xs"
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => {
              const override = buildOverride();
              if (override == null && !hasOverride) {
                setError('Nothing to save — fill at least one field (positive numbers).');
                return;
              }
              void submit(override);
            }}
            className="border-accent-purple text-accent-purple hover:bg-accent-purple/10"
          >
            {pending ? 'Saving…' : 'Apply override'}
          </Button>
          {hasOverride && (
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => void submit(null)}
              className="border-border text-fg-secondary hover:border-status-error hover:text-status-error"
            >
              Clear — back to global profile
            </Button>
          )}
          {error && <span className="text-xs text-status-error">{error}</span>}
        </div>
      </CardContent>
    </Card>
  );
};

type CameraSelectorValue = 'back' | 'front' | 'external';

const CAMERA_SELECTOR_LABELS: Record<CameraSelectorValue, string> = {
  back: 'Phone back (default)',
  front: 'Phone front',
  external: 'External USB camera',
};

/**
 * Camera-selection rollout — operator-controlled per-device camera
 * picker. The selector is a lens facing, not a Camera2 id (external
 * ids are HAL-assigned per device).
 *
 * The backend writes FleetDevice.camera_selector (source of truth)
 * and enqueues a set_camera_selector fleet command in the same
 * request. Phone-side CameraSelectorStore picks the value up on the
 * next recording session; if the selected camera isn't attached at
 * start, the phone falls back to the back camera and mirrors the
 * delta in its capture status.
 */
/**
 * ONE camera card (field feedback 2026-07-03: the selector card + the
 * probed-camera list inside Storage & uplink read as duplicate camera
 * UIs). Selector (phones — set_camera_selector IS implemented on the
 * app) + the reported camera list + Re-probe together.
 *
 * Kind-aware controls: `cameras.snapshot` works on boxes and (since
 * A3, M5 batch) phones — idle-only on phones; recording or pre-A3
 * APKs fail the command with a readable error, never a timeout.
 * `cameras.toggle` / `cameras.refresh` stay BOX-era commands the
 * phone app never implemented, so phones keep info-only rows there;
 * boxes keep toggle / Re-probe.
 */
const CameraCard: React.FC<{
  device: FleetDevice;
  onRefresh: () => void;
  onCommand: (kind: string, payload?: Record<string, any>) => Promise<void>;
  onPreview: (camera: FleetCamera) => void;
}> = ({ device, onRefresh, onCommand, onPreview }) => {
  const isPhone = device.kind === 'phone';
  const current: CameraSelectorValue =
    device.camera_selector === 'front' || device.camera_selector === 'external'
      ? device.camera_selector
      : 'back';
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stabPending, setStabPending] = useState(false);
  const [stabError, setStabError] = useState<string | null>(null);
  const [optToggle, setOptToggle] = useState<Record<string, boolean>>({});

  // Reconcile optimistic toggles with the latest snapshot: once the
  // server-confirmed flag matches (or the camera disappears) drop the
  // override so the switch is prop-driven again.
  useEffect(() => {
    setOptToggle((prev) => {
      const next: Record<string, boolean> = {};
      for (const [id, value] of Object.entries(prev)) {
        const cam = device.cameras.find((c) => c.camera_id === id);
        if (cam && (cam.streaming_enabled && cam.recording_enabled) === value) continue;
        if (cam) next[id] = value;
      }
      return next;
    });
  }, [device.cameras]);

  const handleChange = async (value: CameraSelectorValue) => {
    setPending(true);
    setError(null);
    try {
      await api.setDeviceCameraSelector(
        device.device_id,
        value === 'back' ? null : value,
      );
      onRefresh();
    } catch (e) {
      const msg = (e as { message?: string })?.message ?? String(e);
      setError(msg);
    } finally {
      setPending(false);
    }
  };

  const enabledCameras = device.cameras.filter(
    (c) => c.streaming_enabled || c.recording_enabled,
  ).length;

  return (
    <Card className="border-border bg-bg-surface">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <h3 className="text-base font-medium text-accent-purple">
              Camera
              {device.cameras.length > 0 &&
                !isPhone &&
                ` (${enabledCameras}/${device.cameras.length} enabled)`}
            </h3>
            <HelpTip>
              {isPhone ? (
                <>
                  Which camera the phone records from — applies on the next
                  recording session; if the selected camera isn&apos;t
                  attached at start, the phone falls back to its back camera
                  automatically. The list below is what the phone last
                  reported. The lens button grabs a one-frame preview from
                  the phone — only while it isn&apos;t recording.
                </>
              ) : (
                <>
                  Cameras the box last probed. Toggle to enable/disable a
                  camera, use the lens button for a one-frame preview, and
                  Re-probe after plugging hardware.
                </>
              )}
            </HelpTip>
          </div>
          {!isPhone && (
            <Button
              size="sm"
              variant="outline"
              disabled={!device.online}
              onClick={async () => {
                await onCommand('cameras.refresh');
                setTimeout(() => onRefresh(), 1500);
              }}
            >
              Re-probe
            </Button>
          )}
        </div>

        {isPhone && (
          <Select
            value={current}
            disabled={pending}
            onValueChange={(value) => handleChange(value as CameraSelectorValue)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(CAMERA_SELECTOR_LABELS) as CameraSelectorValue[]).map((value) => (
                <SelectItem key={value} value={value}>
                  {CAMERA_SELECTOR_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {error && (
          <p className="mt-2 text-xs text-status-error">Save failed: {error}</p>
        )}

        {isPhone && (
          <div className="mt-3">
            <div className="mb-1 flex items-center gap-1.5">
              <span className="text-xs font-medium text-fg-secondary">
                Video stabilization
              </span>
              <HelpTip>
                Ladder: this override → fleet default (Recording settings)
                → Auto. The phone validates the mode against its camera and
                falls back safely; applies on the NEXT recording session,
                never mid-recording. OIS (optical) can&apos;t warp the image
                — try it against rolling-shutter &quot;jello&quot; before
                EIS.
              </HelpTip>
            </div>
            <Select
              value={device.stabilization_override ?? 'default'}
              disabled={stabPending}
              onValueChange={(value) => {
                void (async () => {
                  setStabPending(true);
                  setStabError(null);
                  try {
                    await api.setDeviceStabilizationOverride(
                      device.device_id,
                      value === 'default'
                        ? null
                        : (value as 'off' | 'auto' | 'eis' | 'ois' | 'both'),
                    );
                    onRefresh();
                  } catch (e) {
                    setStabError((e as { message?: string })?.message ?? String(e));
                  } finally {
                    setStabPending(false);
                  }
                })();
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Fleet default</SelectItem>
                <SelectItem value="auto">Auto (prefer EIS)</SelectItem>
                <SelectItem value="off">Off</SelectItem>
                <SelectItem value="eis">EIS (electronic)</SelectItem>
                <SelectItem value="ois">OIS (optical)</SelectItem>
                <SelectItem value="both">EIS + OIS</SelectItem>
              </SelectContent>
            </Select>
            {stabError && (
              <p className="mt-1 text-xs text-status-error">Save failed: {stabError}</p>
            )}
          </div>
        )}

        {device.cameras.length > 0 && (
          <div className={cn('space-y-2', isPhone && 'mt-3')}>
            <TooltipProvider>
              {device.cameras.map((c: FleetCamera) => {
                const noSignal = c.is_hdmi && c.signal_locked === false;
                const lockedSignal = c.is_hdmi && c.signal_locked === true;
                return (
                  <div
                    key={c.camera_id}
                    className={cn(
                      'flex items-center gap-2 rounded border bg-bg-elevated px-2 py-1',
                      noSignal ? 'border-status-warning/50' : 'border-border',
                    )}
                  >
                    {c.is_hdmi &&
                      (lockedSignal ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SignalHigh className="size-4 text-status-online" />
                          </TooltipTrigger>
                          <TooltipContent>HDMI signal locked</TooltipContent>
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SignalLow className="size-4 text-status-warning" />
                          </TooltipTrigger>
                          <TooltipContent>HDMI no signal</TooltipContent>
                        </Tooltip>
                      ))}
                    <span
                      className={cn(
                        'flex-1 font-mono',
                        noSignal ? 'text-fg-secondary' : 'text-fg',
                      )}
                    >
                      {c.name || c.camera_id}
                    </span>
                    <span className="text-xs text-fg-muted">
                      {c.type} {c.resolution} @{c.framerate}
                    </span>
                    {/* Preview works on boxes AND phones (A3 shipped
                        cameras.snapshot on the app — idle-only: the
                        phone refuses with a clear error while
                        recording; pre-A3 APKs fail fast with "handler
                        not implemented"). The enable/disable toggle
                        stays box-only. */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            size="icon"
                            variant="ghost"
                            disabled={!device.online}
                            onClick={() => onPreview(c)}
                            className="text-status-buffered"
                            aria-label="Preview frame"
                          >
                            <Camera />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {isPhone ? 'Preview frame (device must be idle)' : 'Preview frame'}
                      </TooltipContent>
                    </Tooltip>
                    {!isPhone && (
                      <>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Switch
                                checked={
                                  optToggle[c.camera_id] ??
                                  (c.streaming_enabled && c.recording_enabled)
                                }
                                disabled={!device.online}
                                onCheckedChange={(enabled) => {
                                  setOptToggle((prev) => ({ ...prev, [c.camera_id]: enabled }));
                                  onCommand('cameras.toggle', {
                                    camera_id: c.name || c.camera_id,
                                    streaming_enabled: enabled,
                                    recording_enabled: enabled,
                                  }).then(() => setTimeout(() => onRefresh(), 1500));
                                }}
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {c.streaming_enabled ? 'Disable camera' : 'Enable camera'}
                          </TooltipContent>
                        </Tooltip>
                      </>
                    )}
                  </div>
                );
              })}
            </TooltipProvider>
          </div>
        )}
        {device.cameras.length === 0 && (
          <p className="mt-3 text-xs text-fg-muted">no cameras reported yet</p>
        )}
      </CardContent>
    </Card>
  );
};


type VideoRotationValue = 0 | 90 | 180 | 270;

const VIDEO_ROTATION_LABELS: Record<VideoRotationValue, string> = {
  0: 'No correction (0\u00b0)',
  90: 'Rotate 90\u00b0 CW',
  180: 'Rotate 180\u00b0',
  270: 'Rotate 270\u00b0 CW',
};

/**
 * Orientation-correction — operator-set per-device DEFAULT display
 * rotation. Display-only: the public viewer rotates playback via a CSS
 * transform (no re-encode, no phone round-trip). A per-session override
 * (set from the dashboard player's \u21bb button) takes precedence.
 */
const VideoRotationCard: React.FC<CodecPreferenceProps> = ({ device, onRefresh }) => {
  const current: VideoRotationValue =
    device.video_rotation === 90 ||
    device.video_rotation === 180 ||
    device.video_rotation === 270
      ? device.video_rotation
      : 0;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (value: VideoRotationValue) => {
    setPending(true);
    setError(null);
    try {
      await api.setDeviceRotation(device.device_id, value === 0 ? null : value);
      onRefresh();
    } catch (e) {
      const msg = (e as { message?: string })?.message ?? String(e);
      setError(msg);
    } finally {
      setPending(false);
    }
  };

  return (
    <Card className="border-border bg-bg-surface">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-1.5">
          <h3 className="text-base font-medium text-accent-purple">Video orientation</h3>
          <HelpTip>
            Corrects how this device&apos;s footage is rotated in the public
            viewer (display-only — no re-encode). Applies to every session
            from this device; a single session can be overridden from the
            player&apos;s rotate button.
          </HelpTip>
        </div>
        <Select
          value={String(current)}
          disabled={pending}
          onValueChange={(value) => handleChange(Number(value) as VideoRotationValue)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(VIDEO_ROTATION_LABELS) as unknown as VideoRotationValue[]).map(
              (value) => (
                <SelectItem key={value} value={String(value)}>
                  {VIDEO_ROTATION_LABELS[value]}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
        {error && (
          <p className="mt-2 text-xs text-status-error">Save failed: {error}</p>
        )}
      </CardContent>
    </Card>
  );
};
export const DeviceDetail: React.FC<DeviceDetailProps> = ({
  device,
  onBack,
  onCommand,
  onRefresh,
}) => {
  const [bitrate, setBitrate] = useState<number>(4);
  const [codec, setCodec] = useState<'h265' | 'h264'>('h265');
  const [commands, setCommands] = useState<FleetCommand[]>([]);
  const [previewCamera, setPreviewCamera] = useState<FleetCamera | null>(null);
  const [shareOpen, setShareOpen] = useState<boolean>(false);
  const [restartOpen, setRestartOpen] = useState<boolean>(false);
  // Two-step arm for the destructive "Cancel uploads" (drops in-flight
  // segments) — a bare click fired it with no confirmation.
  const [cancelUploadsArmed, setCancelUploadsArmed] = useState(false);
  // Per-session ⋯ action menu (Recent sessions rows).
  const [sessionMenuFor, setSessionMenuFor] = useState<string | null>(null);
  // M2 — per-session upload pause. The §7 cloud mirror
  // (FleetSession.upload_paused, served on the sessions rows) is the
  // display truth; this map only tracks in-flight/error per session.
  const [pauseState, setPauseState] = useState<Record<string, 'sending' | 'error'>>({});
  const cancelUploadsTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (cancelUploadsTimer.current != null) window.clearTimeout(cancelUploadsTimer.current);
    },
    [],
  );
  // Phase 2.B.3 / 2.B.5 — operator-visible lists, read-only for now.
  const [incidents, setIncidents] = useState<FleetIncident[]>([]);
  const [archiveRequests, setArchiveRequests] = useState<FleetArchiveRequest[]>([]);
  // Phase 2.B.2 — Recent sessions for this device (V2 FleetSession +
  // legacy S3 prefix-match unioned by the backend). Scoped to the
  // current event by default (rental/handover privacy); "all events"
  // is the explicit admin escape.
  const { currentEventId } = useCurrentEvent();
  const [allHistory, setAllHistory] = useState(false);
  const sessionsEventId = allHistory ? null : currentEventId;
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  // Rename (inline edit of device.name) + session delete.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameOverride, setNameOverride] = useState<string | null | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<DeviceSession | null>(null);
  useEffect(() => {
    setNameOverride(undefined);
    setEditingName(false);
  }, [device.device_id]);
  const deviceName = nameOverride !== undefined ? nameOverride : device.name;
  const submitRename = async () => {
    const next = nameDraft.trim();
    setEditingName(false);
    if (next === (deviceName ?? '')) return;
    setNameOverride(next || null);
    try {
      const updated = await api.renameDevice(device.device_id, next || null);
      setNameOverride(updated.name ?? null);
      onRefresh();
    } catch {
      setNameOverride(undefined);
    }
  };
  // Phase 5 B1 — per-session "Upload now" button state (allow a session
  // to drain over a metered link on demand). Keyed by session_id.
  const [uploadNowState, setUploadNowState] = useState<
    Record<string, 'sending' | 'sent' | 'error'>
  >({});
  // Per-session manual-link button state (join a restart-split session
  // with the previous one into a single run). Keyed by session_id.
  const [linkState, setLinkState] = useState<
    Record<string, 'sending' | 'error'>
  >({});
  // M6 clips — inline "Request clip" range draft (seconds into the
  // session; blank end = to the end) + submit state. One draft at a
  // time, keyed to the session whose ⋯ menu opened it.
  const [clipDraft, setClipDraft] = useState<{
    sessionId: string;
    from: string;
    to: string;
  } | null>(null);
  const [clipState, setClipState] = useState<
    Record<string, 'sending' | 'sent' | 'error'>
  >({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listFleetDeviceCommands(device.device_id, 50)
      .then((rows) => {
        if (!cancelled) setCommands(rows);
      })
      .catch(() => undefined);
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (evt.type === 'command_update' && evt.command.device_id === device.device_id) {
        setCommands((prev) => {
          const idx = prev.findIndex((c) => c.uuid === evt.command.uuid);
          if (idx >= 0) {
            const next = prev.slice();
            next[idx] = evt.command;
            return next;
          }
          return [evt.command, ...prev].slice(0, 50);
        });
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [device.device_id]);

  // Phase 2.B.3 / 2.B.5 — poll archive requests + incidents.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const [arc, inc, ses] = await Promise.all([
          api.listDeviceArchiveRequests(device.device_id, 10),
          api.listDeviceIncidents(device.device_id, 25),
          api.listDeviceSessions(device.device_id, sessionsEventId),
        ]);
        if (!cancelled) {
          setArchiveRequests(arc);
          setIncidents(inc);
          setSessions(ses);
        }
      } catch {
        // ignore — endpoints may not exist on older backends
      }
    };
    void refresh();
    const id = window.setInterval(refresh, 10000);
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (
        evt.type === 'command_update' &&
        evt.command.device_id === device.device_id &&
        (evt.command.kind === 'request_archive' ||
          evt.command.kind === 'archive_acked' ||
          evt.command.kind === 'incident_confirm')
      ) {
        void refresh();
      }
    });
    return () => {
      cancelled = true;
      window.clearInterval(id);
      unsub();
    };
  }, [device.device_id, sessionsEventId]);

  // M6 — operator keep/discard on a pending incident. Optimistic row
  // update; the 10 s poll (plus the incident_confirm command_update
  // hook above) reconciles with the server state.
  const handleIncidentDecision = async (
    incidentUuid: string,
    decision: 'keep' | 'discard',
  ) => {
    setIncidents((prev) =>
      prev.map((i) =>
        i.incident_uuid === incidentUuid
          ? { ...i, state: decision === 'keep' ? 'kept' : 'discarded', decision }
          : i,
      ),
    );
    try {
      await api.confirmIncident(incidentUuid, decision);
    } catch {
      // poll reconciles the real state; nothing actionable here
    }
  };

  const sendBitrate = (value: number) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onCommand('settings.update', { 'streaming.bitrate_mbps': value });
    }, 600);
  };

  const sendCodec = (value: 'h265' | 'h264') => {
    onCommand('settings.update', { 'buffered_mode.hls_codec': value });
  };

  const startSelected = (kind: 'buffered.start' | 'streaming.start') => {
    onCommand(kind, {
      cameras: device.cameras
        .filter((c) => c.streaming_enabled)
        .map((c) => c.name || c.camera_id),
    });
  };

  const stop = () => {
    onCommand(device.mode === 'live' ? 'streaming.stop' : 'buffered.stop');
  };

  const cancelCommand = async (uuid: string) => {
    try {
      await api.cancelFleetCommand(device.device_id, uuid);
      // SSE `command_update` will refresh the row in-place; no manual refetch.
    } catch (err) {
      console.warn('cancelFleetCommand failed', err);
    }
  };

  // Queued commands waiting on the device — surfaced as a small chip
  // next to Record/Stop so the operator sees the offline-queue state
  // without scrolling to the Recent commands table.
  const queuedCommands = useMemo(
    () => commands.filter((c) => c.status === 'queued'),
    [commands],
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="text-accent"
          aria-label="Back"
        >
          <ArrowLeft />
        </Button>
        {device.online ? (
          <Wifi className="size-5 text-status-online" />
        ) : (
          <WifiOff className="size-5 text-fg-muted" />
        )}
        {editingName ? (
          <div className="flex items-center gap-1">
            <Input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitRename();
                if (e.key === 'Escape') setEditingName(false);
              }}
              placeholder={device.hostname || device.device_id}
              maxLength={255}
              className="h-8 w-56 text-base"
            />
            <Button
              variant="ghost"
              size="icon"
              className="text-status-online"
              onClick={() => void submitRename()}
              aria-label="Save name"
            >
              <Check className="size-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-baseline gap-1.5">
            <h1 className="font-mono text-xl text-accent">{deviceName || device.device_id}</h1>
            {deviceName && (
              <span className="font-mono text-[11px] text-fg-muted">{device.device_id}</span>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-fg-muted hover:text-fg"
              onClick={() => {
                setNameDraft(deviceName ?? '');
                setEditingName(true);
              }}
              aria-label="Rename device"
              title="Rename device"
            >
              <Pencil className="size-3.5" />
            </Button>
          </div>
        )}
        <Badge
          variant="outline"
          className={cn(
            'font-mono',
            device.online
              ? 'border-status-online text-status-online'
              : 'border-fg-muted text-fg-muted',
          )}
        >
          {device.status}
        </Badge>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="font-mono text-fg-secondary">
                {device.online ? 'online' : 'last seen'} {formatRelative(device.last_seen_at)}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {device.online
                ? `Connected since the latest heartbeat (${device.last_ws_at ?? device.last_seen_at ?? '—'})`
                : `Last contact: ${device.last_seen_at ?? 'never'}`}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="flex-1" />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={onRefresh} aria-label="Refresh">
                <RefreshCw />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Refresh</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Row partners stay equal-height (default stretch) — items-start
          left ragged card bottoms (Kris). Cards are kept naturally
          compact instead: explainer copy lives in HelpTip icons. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Mode + controls — chips ride the title row to keep the card
            one compact block. */}
        <Card className="border-border bg-bg-surface">
          <CardContent className="p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <h2 className="text-base font-medium text-accent">Mode</h2>
              <Badge
                variant="outline"
                className={cn(
                  'font-mono',
                  device.mode === 'idle' || !device.mode
                    ? 'border-border text-fg-muted'
                    : 'border-status-buffered text-status-buffered',
                )}
              >
                {device.mode || 'idle'}
              </Badge>
              {device.current_session_id && (
                <Badge variant="outline" className="font-mono text-fg">
                  session {device.current_session_id}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <TooltipProvider>
                {device.kind === 'phone' ? (
                  <>
                    {/* Phone Record/Stop intentionally enabled while offline.
                        Commands queue server-side and drain on next WS
                        connect; the small "queued" chip below makes that
                        explicit. Operator can still cancel from the
                        Recent commands table. */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="outline"
                            disabled={device.mode === 'buffered'}
                            onClick={() => onCommand('phone.start_recording', {})}
                            className="border-accent-purple text-accent-purple hover:bg-accent-purple/10"
                          >
                            <RecordCircle />
                            Record
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {device.online
                          ? 'Tell the phone to start recording'
                          : 'Phone offline — command will queue and run on reconnect'}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="outline"
                            disabled={device.mode === 'idle' || device.mode == null}
                            onClick={() => onCommand('phone.stop_recording', {})}
                            className="border-status-error text-status-error hover:bg-status-error/10"
                          >
                            <Square />
                            Stop
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {device.online
                          ? 'Stop phone recording'
                          : 'Phone offline — stop will queue and run on reconnect'}
                      </TooltipContent>
                    </Tooltip>
                    {queuedCommands.length > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="outline"
                            className="border-status-warning font-mono text-status-warning"
                          >
                            {queuedCommands.length} queued
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          {queuedCommands.length} command{queuedCommands.length === 1 ? '' : 's'} waiting on the device.
                          Cancel individual rows in the Recent commands table.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      disabled={!device.online || device.mode === 'buffered'}
                      onClick={() => startSelected('buffered.start')}
                      className="border-status-buffered text-status-buffered hover:bg-status-buffered/10"
                    >
                      <RecordCircle />
                      Start Buffered
                    </Button>
                    <Button
                      variant="outline"
                      disabled={!device.online || device.mode === 'live'}
                      onClick={() => startSelected('streaming.start')}
                      className="border-status-online text-status-online hover:bg-status-online/10"
                    >
                      <Play />
                      Start Live
                    </Button>
                    <Button
                      variant="outline"
                      disabled={!device.online || device.mode === 'idle' || device.mode == null}
                      onClick={stop}
                      className="border-status-error text-status-error hover:bg-status-error/10"
                    >
                      <Square />
                      Stop
                    </Button>
                  </>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        disabled={!device.current_session_id}
                        onClick={() => setShareOpen(true)}
                        className="border-accent-purple text-accent-purple hover:bg-accent-purple/10"
                      >
                        <Share2 />
                        Share
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {device.current_session_id
                      ? 'Mint signed share link for this session'
                      : 'Start a session before sharing'}
                  </TooltipContent>
                </Tooltip>
                {device.kind !== 'phone' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          variant="outline"
                          disabled={!device.online}
                          onClick={() => setRestartOpen(true)}
                          className="border-status-warning text-status-warning hover:bg-status-warning/10"
                        >
                          <RotateCcw />
                          Restart
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Restart controller / reboot box</TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>

            {device.kind !== 'phone' && (
              <>
                <div className="my-6 h-px bg-border" />
                <h3 className="mb-2 text-base font-medium text-accent">Live bitrate</h3>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Slider
                      value={[bitrate]}
                      min={1}
                      max={25}
                      step={0.5}
                      onValueChange={(vals) => {
                        const value = vals[0];
                        setBitrate(value);
                        sendBitrate(value);
                      }}
                    />
                  </div>
                  <span className="min-w-[60px] font-mono text-fg">{bitrate.toFixed(1)} Mb/s</span>
                </div>

                <h3 className="mt-6 mb-2 text-base font-medium text-accent">Buffered HLS codec</h3>
                <Select
                  value={codec}
                  onValueChange={(v) => {
                    const value = v as 'h265' | 'h264';
                    setCodec(value);
                    sendCodec(value);
                  }}
                >
                  <SelectTrigger className="w-full font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="h265">h265 (high quality, no Chrome)</SelectItem>
                    <SelectItem value="h264">h264 (browser playable)</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </CardContent>
        </Card>

        {/* Storage + cameras */}
        <Card className="border-border bg-bg-surface">
          <CardContent className="p-4">
            <h2 className="mb-3 text-base font-medium text-accent">Storage & uplink</h2>
            {/* Storage only reports while connected and isn't remembered
                across reconnects — "— free / —" rows read as broken
                (field feedback). One honest muted line when unknown;
                last-reported persistence is a backend backlog item. */}
            <div className="mb-2 flex items-center gap-2">
              <HardDrive className="size-4 text-fg-muted" />
              {device.disk_free_bytes != null || device.disk_total_bytes != null ? (
                <span className="font-mono text-fg">
                  {formatBytes(device.disk_free_bytes)} free / {formatBytes(device.disk_total_bytes)}
                  {/* M1: sticky value — age it while offline. */}
                  {!device.online && device.disk_reported_at && (
                    <span className="ml-1.5 text-xs text-fg-muted">
                      as of {formatRelative(device.disk_reported_at)}
                    </span>
                  )}
                </span>
              ) : (
                <span className="font-mono text-xs text-fg-muted">
                  no storage report yet{device.online ? '' : ' — offline'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <MemoryStick className="size-4 text-fg-muted" />
              <span className="flex-1 font-mono text-fg">
                uploads pending:{' '}
                {device.upload_pending != null ? (
                  device.upload_pending
                ) : (
                  <span className="text-xs text-fg-muted">no report yet</span>
                )}
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!device.online || (device.upload_pending ?? 0) === 0}
                        onClick={() => {
                          // First click arms; second click (within 4 s)
                          // fires. Destructive — drops in-flight uploads.
                          if (!cancelUploadsArmed) {
                            setCancelUploadsArmed(true);
                            if (cancelUploadsTimer.current != null) {
                              window.clearTimeout(cancelUploadsTimer.current);
                            }
                            cancelUploadsTimer.current = window.setTimeout(
                              () => setCancelUploadsArmed(false),
                              4000,
                            );
                            return;
                          }
                          setCancelUploadsArmed(false);
                          void onCommand('upload.cancel_all', {});
                        }}
                        className={cn(
                          'border-status-error text-status-error hover:bg-status-error/10',
                          cancelUploadsArmed && 'bg-status-error/15 font-bold',
                        )}
                      >
                        {/* "ALL" spelled out — per-session cancel/resume
                            is the real want (backlog, needs backend). */}
                        {cancelUploadsArmed ? 'Click again to confirm' : 'Cancel ALL uploads'}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Hard stop — clear queue + drop in-flight uploads
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Per-tier backlog so operator can tell at a glance whether
                the queue is "the last few low-quality segments draining"
                (fine — keep app open) vs "10 GB of high-quality backlog"
                (problem —
                check wifi). Same data the Phone capture card badges
                show, surfaced where the operator looks for upload health. */}
            {(device.upload_backlog_stream_mb != null || device.upload_backlog_archive_mb != null) && (
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded border border-border bg-bg-elevated px-2 py-1">
                  <div className="text-[10px] uppercase tracking-wider text-fg-muted">
                    low quality backlog
                  </div>
                  <div className="font-mono text-fg">
                    {device.upload_backlog_stream_mb != null
                      ? `${device.upload_backlog_stream_mb.toFixed(1)} MB`
                      : '—'}
                  </div>
                </div>
                <div className="rounded border border-border bg-bg-elevated px-2 py-1">
                  <div className="text-[10px] uppercase tracking-wider text-fg-muted">
                    high quality backlog
                  </div>
                  <div className="font-mono text-fg">
                    {device.upload_backlog_archive_mb != null
                      ? `${device.upload_backlog_archive_mb.toFixed(1)} MB`
                      : '—'}
                  </div>
                </div>
              </div>
            )}

          </CardContent>
        </Card>

        {/* Row pairing tuned for even heights (field feedback):
            [Mode | Storage & uplink] · [Camera | Phone capture state] ·
            [Encoder codec | Video orientation] · full-width rows after. */}
        <CameraCard
          device={device}
          onRefresh={onRefresh}
          onCommand={onCommand}
          onPreview={setPreviewCamera}
        />

        {device.kind === 'phone' && <PhoneCaptureSettings device={device} />}

        {device.kind === 'phone' && (
          <CodecPreferenceCard device={device} onRefresh={onRefresh} />
        )}

        {device.kind === 'phone' && (
          <VideoRotationCard device={device} onRefresh={onRefresh} />
        )}

        {device.kind !== 'phone' && (
          <div className="md:col-span-2">
            <ConnectionPanel device={device} onCommand={onCommand} />
          </div>
        )}

        {device.kind === 'phone' && (
          <QualityOverrideCard device={device} onRefresh={onRefresh} />
        )}

        {device.kind === 'phone' && <SpeedTestCard device={device} />}

        {device.kind === 'phone' && (
          // Full-width: as a half-column card it sat alone on the last
          // odd row with an empty partner slot.
          <div className="md:col-span-2">
            <UploadTelemetryPanel deviceId={device.device_id} />
          </div>
        )}

        {/* Phase 2.B.2 recent sessions — newest-first. ▶ Play takes the
            operator to /play/<sid> which mounts SyncedViewerPanel against
            the buffered playlist. */}
        <Card className="border-border bg-bg-surface md:col-span-2">
          <CardContent className="p-4">
            <h2 className="mb-3 flex items-center gap-2 text-base font-medium text-accent">
              Recent sessions ({sessions.length})
              {currentEventId != null && (
                <button
                  type="button"
                  onClick={() => setAllHistory((v) => !v)}
                  title={
                    allHistory
                      ? 'Showing every session this device ever recorded — click to scope to the current event'
                      : 'Scoped to the current event — click to show all history (incl. other events)'
                  }
                  className={cn(
                    'rounded-sm border px-1.5 py-px font-mono text-[10px] uppercase tracking-wide',
                    allHistory
                      ? 'border-status-warning text-status-warning'
                      : 'border-border text-fg-muted hover:text-fg',
                  )}
                >
                  {allHistory ? 'showing all events' : 'this event only'}
                </button>
              )}
            </h2>
            {sessions.length === 0 ? (
              <span className="text-xs text-fg-muted">no sessions yet</span>
            ) : (
              // md+: one row per session. Phones: the row WRAPS (id on
              // its own line, actions below) instead of forcing a 680px
              // horizontal scroll — field ops + self-service users run
              // this page on their phone.
              <div className="overflow-x-auto">
              <div className="space-y-1 md:min-w-[680px]">
                {sessions.slice(0, 20).map((s, i) => (
                  <div
                    key={s.session_id}
                    className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-border/50 px-2 py-2 md:flex-nowrap"
                  >
                    <span className="w-full font-mono text-sm text-fg md:w-auto md:min-w-[220px]">
                      {s.session_id}
                      {s.continues_session_id && (
                        <span
                          className="ml-2 text-[10px] text-status-online"
                          title={`Joined — continues ${s.continues_session_id}`}
                        >
                          ⛓ joined
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-fg-muted md:min-w-[160px]">
                      {s.started_at || s.last_update || '—'}
                    </span>
                    <Badge variant="outline" className="min-w-[80px] justify-center font-mono">
                      {s.segment_count} seg
                    </Badge>
                    {/* M2 — per-session upload accounting (uploaded MB is
                        exact; the pending amount is the device backlog at
                        this session's newest sample → labeled ≈). */}
                    {s.uploaded_mb != null && (
                      <span
                        className="whitespace-nowrap font-mono text-[11px] text-fg-secondary"
                        title="MB received by the backend for this session · pending = exact chunk counts from the phone (A1); ≈ = device backlog at this session's last report (pre-A1 fallback)"
                      >
                        {s.uploaded_mb.toFixed(1)} MB up
                        {/* A1 exact counts win; ≈MB only pre-A1-APK. */}
                        {s.pending_stream != null || s.pending_archive != null ? (
                          ((s.pending_stream ?? 0) + (s.pending_archive ?? 0) > 0 && (
                            <span className="text-status-warning">
                              {' '}· {(s.pending_stream ?? 0) + (s.pending_archive ?? 0)} chunks left
                            </span>
                          ))
                        ) : (
                          s.last_backlog_mb != null && s.last_backlog_mb > 0 && (
                            <span className="text-status-warning">
                              {' '}· ≈{s.last_backlog_mb.toFixed(1)} left
                            </span>
                          )
                        )}
                      </span>
                    )}
                    {s.upload_paused && (
                      <Badge
                        variant="outline"
                        className="whitespace-nowrap border-status-warning/60 font-mono text-[10px] text-status-warning"
                        title="Uploads paused for this session (cloud mirror; the phone syncs via set_session_upload_pause)"
                      >
                        ⏸ paused
                      </Badge>
                    )}
                    <span className="flex-1 font-mono text-xs text-fg-secondary">
                      {s.cameras.join(', ') || '—'}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        window.location.href = `/play/${encodeURIComponent(s.session_id)}`;
                      }}
                      className="min-w-[80px] border-border font-mono text-accent-purple hover:border-accent-purple hover:bg-accent-purple/10"
                    >
                      ▶ Play
                    </Button>
                    {/* Secondary actions live in a per-row ⋯ menu —
                        Upload / Join / Delete inline was becoming a
                        button wall (field feedback 2026-07-03). Menu
                        stays open through Upload/Join so the operator
                        sees sending → sent/failed. */}
                    <div className="relative">
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label={`Session actions for ${s.session_id}`}
                        aria-expanded={sessionMenuFor === s.session_id}
                        onClick={() =>
                          setSessionMenuFor(
                            sessionMenuFor === s.session_id ? null : s.session_id,
                          )
                        }
                        className="border-border px-2.5 font-mono text-xs text-fg-secondary hover:border-accent hover:text-fg"
                      >
                        ⋯
                      </Button>
                      {sessionMenuFor === s.session_id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setSessionMenuFor(null)}
                          />
                          <div className="absolute right-0 top-9 z-50 w-56 overflow-hidden rounded-md border border-border bg-bg-elevated py-1 shadow-modal">
                            <button
                              type="button"
                              disabled={uploadNowState[s.session_id] === 'sending'}
                              title="Allow this session to upload over a metered link now"
                              onClick={async () => {
                                setUploadNowState((m) => ({ ...m, [s.session_id]: 'sending' }));
                                try {
                                  await api.uploadNow(s.session_id);
                                  setUploadNowState((m) => ({ ...m, [s.session_id]: 'sent' }));
                                } catch {
                                  setUploadNowState((m) => ({ ...m, [s.session_id]: 'error' }));
                                }
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left font-mono text-xs text-fg hover:bg-bg-surface disabled:opacity-40"
                            >
                              {uploadNowState[s.session_id] === 'sending'
                                ? 'sending…'
                                : uploadNowState[s.session_id] === 'sent'
                                  ? 'Upload now — sent ✓'
                                  : uploadNowState[s.session_id] === 'error'
                                    ? 'Upload now — failed'
                                    : '📶 Upload now'}
                            </button>
                            {/* M6 clips — request the full-quality
                                archive segments for a time range; the
                                phone re-uploads them through tus and
                                the request shows in the Clip requests
                                card below. 6 s segment granularity. */}
                            {clipDraft?.sessionId === s.session_id ? (
                              <div className="flex items-center gap-1 px-3 py-2">
                                <input
                                  type="number"
                                  min={0}
                                  placeholder="from s"
                                  value={clipDraft.from}
                                  onChange={(e) =>
                                    setClipDraft({ ...clipDraft, from: e.target.value })
                                  }
                                  className="w-16 rounded border border-border bg-bg-base px-1 py-0.5 font-mono text-xs text-fg"
                                  aria-label="Clip start, seconds into session"
                                />
                                <span className="text-xs text-fg-muted">–</span>
                                <input
                                  type="number"
                                  min={0}
                                  placeholder="end"
                                  value={clipDraft.to}
                                  onChange={(e) =>
                                    setClipDraft({ ...clipDraft, to: e.target.value })
                                  }
                                  className="w-16 rounded border border-border bg-bg-base px-1 py-0.5 font-mono text-xs text-fg"
                                  aria-label="Clip end, seconds into session (blank = to end)"
                                />
                                <button
                                  type="button"
                                  disabled={clipState[s.session_id] === 'sending'}
                                  onClick={async () => {
                                    const fromS = Math.max(0, Number(clipDraft.from) || 0);
                                    const toS = clipDraft.to.trim() ? Number(clipDraft.to) : null;
                                    if (toS != null && (!Number.isFinite(toS) || toS <= fromS)) return;
                                    setClipState((m) => ({ ...m, [s.session_id]: 'sending' }));
                                    try {
                                      await api.createArchiveRequest(device.device_id, {
                                        session_id: s.session_id,
                                        camera_id: s.cameras[0] || 'phone-rear',
                                        seg_start: Math.floor(fromS / 6),
                                        seg_end: toS == null ? -1 : Math.ceil(toS / 6),
                                      });
                                      setClipState((m) => ({ ...m, [s.session_id]: 'sent' }));
                                      setClipDraft(null);
                                    } catch {
                                      setClipState((m) => ({ ...m, [s.session_id]: 'error' }));
                                    }
                                  }}
                                  className="rounded border border-border px-2 py-0.5 font-mono text-xs text-accent-purple hover:bg-bg-surface disabled:opacity-40"
                                >
                                  {clipState[s.session_id] === 'sending' ? '…' : 'Request'}
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                title="Ask the phone to upload the full-quality segments for a time range (seconds into the session)"
                                onClick={() =>
                                  setClipDraft({ sessionId: s.session_id, from: '', to: '' })
                                }
                                className="flex w-full items-center gap-2 px-3 py-2 text-left font-mono text-xs text-fg hover:bg-bg-surface"
                              >
                                {clipState[s.session_id] === 'sent'
                                  ? '🎬 Request clip — sent ✓'
                                  : clipState[s.session_id] === 'error'
                                    ? '🎬 Request clip — failed'
                                    : '🎬 Request clip…'}
                              </button>
                            )}
                            {/* M2 — pause/resume THIS session's uploads
                                (the per-session control "Cancel ALL" never
                                was). §7 endpoint: cloud mirror + phone
                                command; queues while offline. */}
                            <button
                              type="button"
                              disabled={pauseState[s.session_id] === 'sending'}
                              title="Pause or resume uploading of this session's segments (queues if the phone is offline)"
                              onClick={async () => {
                                const next = !s.upload_paused;
                                setPauseState((m) => ({ ...m, [s.session_id]: 'sending' }));
                                try {
                                  const res = await api.setSessionUploadPause(
                                    device.device_id,
                                    s.session_id,
                                    next,
                                  );
                                  setSessions((arr) =>
                                    arr.map((x) =>
                                      x.session_id === s.session_id
                                        ? { ...x, upload_paused: res.paused }
                                        : x,
                                    ),
                                  );
                                  setPauseState((m) => {
                                    const n = { ...m };
                                    delete n[s.session_id];
                                    return n;
                                  });
                                } catch {
                                  setPauseState((m) => ({ ...m, [s.session_id]: 'error' }));
                                }
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left font-mono text-xs text-status-warning hover:bg-bg-surface disabled:opacity-40"
                            >
                              {pauseState[s.session_id] === 'sending'
                                ? s.upload_paused
                                  ? 'resuming…'
                                  : 'pausing…'
                                : pauseState[s.session_id] === 'error'
                                  ? 'pause — failed, retry'
                                  : s.upload_paused
                                    ? '▶ Resume uploads'
                                    : '⏸ Pause uploads'}
                            </button>
                            {s.continues_session_id ? (
                              <button
                                type="button"
                                disabled={linkState[s.session_id] === 'sending'}
                                title={`Joined — continues ${s.continues_session_id}. Click to unlink.`}
                                onClick={async () => {
                                  setLinkState((m) => ({ ...m, [s.session_id]: 'sending' }));
                                  try {
                                    await api.linkSession(device.device_id, s.session_id, null);
                                    setSessions((arr) =>
                                      arr.map((x) =>
                                        x.session_id === s.session_id
                                          ? { ...x, continues_session_id: null }
                                          : x,
                                      ),
                                    );
                                    setLinkState((m) => {
                                      const n = { ...m };
                                      delete n[s.session_id];
                                      return n;
                                    });
                                  } catch {
                                    setLinkState((m) => ({ ...m, [s.session_id]: 'error' }));
                                  }
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left font-mono text-xs text-status-online hover:bg-bg-surface disabled:opacity-40"
                              >
                                {linkState[s.session_id] === 'sending' ? 'unlinking…' : '⛓ Unlink'}
                              </button>
                            ) : sessions[i + 1] ? (
                              <button
                                type="button"
                                disabled={linkState[s.session_id] === 'sending'}
                                title={`Join into one run: mark this as continuing the previous (older) session ${sessions[i + 1].session_id}`}
                                onClick={async () => {
                                  const pred = sessions[i + 1].session_id;
                                  setLinkState((m) => ({ ...m, [s.session_id]: 'sending' }));
                                  try {
                                    await api.linkSession(device.device_id, s.session_id, pred);
                                    setSessions((arr) =>
                                      arr.map((x) =>
                                        x.session_id === s.session_id
                                          ? { ...x, continues_session_id: pred }
                                          : x,
                                      ),
                                    );
                                    setLinkState((m) => {
                                      const n = { ...m };
                                      delete n[s.session_id];
                                      return n;
                                    });
                                  } catch {
                                    setLinkState((m) => ({ ...m, [s.session_id]: 'error' }));
                                  }
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left font-mono text-xs text-fg hover:bg-bg-surface disabled:opacity-40"
                              >
                                {linkState[s.session_id] === 'sending'
                                  ? 'linking…'
                                  : linkState[s.session_id] === 'error'
                                    ? 'join — failed'
                                    : '⛓ Join w/ prev'}
                              </button>
                            ) : null}
                            <button
                              type="button"
                              disabled={s.session_id === device.current_session_id}
                              title={
                                s.session_id === device.current_session_id
                                  ? 'Stop the recording before deleting'
                                  : 'Delete session (removes footage permanently)'
                              }
                              onClick={() => {
                                setSessionMenuFor(null);
                                setDeleteTarget(s);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left font-mono text-xs text-fg-muted hover:bg-status-error/10 hover:text-status-error disabled:opacity-30"
                            >
                              <Trash2 className="size-3.5" />
                              Delete session…
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Phase 2.B.5 incidents — RE-RENDERED for M6 Option A: the
            backend now writes only the curated INCIDENT_KINDS_ENABLED
            set and prunes per device (200 rows / 7 days), so this card
            shows a short reviewable list instead of the old "100
            incidents" wall. Renders only when there IS something to
            review (same posture as clip requests below); pending rows
            get keep/discard, wired to the incident_confirm command. */}
        {incidents.length > 0 && (
        <Card className="border-border bg-bg-surface">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center gap-1.5">
              <h2 className="text-base font-medium text-accent">
                Incidents ({incidents.length})
              </h2>
              <HelpTip>
                Auto-detected mid-stage events (curated kinds only —
                GPS dropout, unexpected stop, hard braking). Keep pins
                the footage around the moment against eviction; Discard
                lets normal cleanup proceed. Rows age out after 7 days.
              </HelpTip>
            </div>
            <div className="space-y-1">
              <TooltipProvider>
                {incidents.slice(0, 15).map((inc) => {
                    const stateClass =
                      inc.state === 'kept'
                        ? 'border-status-online text-status-online'
                        : inc.state === 'discarded'
                          ? 'border-fg-muted text-fg-muted'
                          : inc.state === 'pending'
                            ? 'border-status-warning text-status-warning'
                            : 'border-status-error text-status-error';
                    const tsLabel = new Date(inc.ts_ms).toISOString().replace('T', ' ').slice(0, 19);
                    return (
                      <div
                        key={inc.incident_uuid}
                        className="flex items-center gap-2 border-b border-border/50 px-2 py-1"
                      >
                        <span className="min-w-[100px] font-mono text-fg">{inc.kind}</span>
                        <Badge
                          variant="outline"
                          className={cn('min-w-[80px] justify-center font-mono', stateClass)}
                        >
                          {inc.state}
                        </Badge>
                        <span className="font-mono text-xs text-fg-muted">{tsLabel}</span>
                        {inc.severity != null && (
                          <span className="font-mono text-xs text-fg">
                            sev {inc.severity.toFixed(2)}
                          </span>
                        )}
                        <div className="flex-1" />
                        {inc.description && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="max-w-[160px] overflow-hidden truncate whitespace-nowrap font-mono text-xs text-fg-secondary">
                                {inc.description}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{inc.description}</TooltipContent>
                          </Tooltip>
                        )}
                        {inc.state === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[11px]"
                              onClick={() => void handleIncidentDecision(inc.incident_uuid, 'keep')}
                            >
                              Keep
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-[11px] text-fg-muted"
                              onClick={() => void handleIncidentDecision(inc.incident_uuid, 'discard')}
                            >
                              Discard
                            </Button>
                          </>
                        )}
                      </div>
                    );
                  })}
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Phase 2.B.3 clip requests ("archive" was the internal name;
            operators read it as backups) — read-only, and the card only
            renders when there IS something to show (field feedback:
            feature isn't wired end-to-end yet, an empty always-on card
            just advertised that). */}
        {archiveRequests.length > 0 && (
        <Card className="border-border bg-bg-surface">
          <CardContent className="p-4">
            <h2 className="mb-3 text-base font-medium text-accent">
              Clip requests ({archiveRequests.length})
            </h2>
            {archiveRequests.length === 0 ? (
              <span className="text-xs text-fg-muted">no clip requests</span>
            ) : (
              <div className="space-y-1">
                {archiveRequests.slice(0, 10).map((req) => {
                  const stateClass =
                    req.state === 'acked'
                      ? 'border-status-online text-status-online'
                      : req.state === 'completed'
                        ? 'border-status-buffered text-status-buffered'
                        : req.state === 'requested' || req.state === 'partial'
                          ? 'border-status-warning text-status-warning'
                          : 'border-fg-muted text-fg-muted';
                  const segLabel =
                    req.seg_end === -1
                      ? `segs ${req.seg_start}..*`
                      : `segs ${req.seg_start}..${req.seg_end}`;
                  return (
                    <div
                      key={req.request_uuid}
                      className="flex items-center gap-2 border-b border-border/50 px-2 py-1"
                    >
                      <span className="min-w-[100px] font-mono text-fg">{req.camera_id}</span>
                      <Badge
                        variant="outline"
                        className={cn('min-w-[80px] justify-center font-mono', stateClass)}
                      >
                        {req.state}
                      </Badge>
                      <span className="font-mono text-xs text-fg">{segLabel}</span>
                      <div className="flex-1" />
                      {req.may_evict != null && (
                        <span
                          className={cn(
                            'font-mono text-xs',
                            req.may_evict ? 'text-status-online' : 'text-fg-secondary',
                          )}
                        >
                          {req.may_evict ? 'evict ok' : 'no evict'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        <Card className="border-border bg-bg-surface md:col-span-2">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-base font-medium text-accent">Recent commands</h2>
              <Button
                size="sm"
                variant="outline"
                disabled={!device.online}
                title="Clear the device's geofence consumed_kinds so the same gates can fire again. Useful for repeated test passes through a single-stage event."
                onClick={async () => {
                  await onCommand('phone.reset_stage');
                }}
              >
                Reset stage
              </Button>
            </div>
            {commands.length === 0 ? (
              <span className="text-xs text-fg-muted">no commands yet</span>
            ) : (
              <TooltipProvider>
              {/* Phones (field ops / self-service): the latency columns
                  hide below md so the table reflows instead of forcing
                  a 640px horizontal scroll. Height capped — a long
                  command history used to stretch the page forever
                  (field feedback); scrolls inside the card instead. */}
              <div className="max-h-[420px] overflow-x-auto overflow-y-auto">
              <div className="space-y-1 md:min-w-[640px]">
                {/* Header row */}
                <div className="grid grid-cols-[1fr_80px_minmax(0,1.2fr)_36px] gap-3 border-b border-border px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-muted md:grid-cols-[140px_90px_80px_70px_70px_70px_1fr_36px]">
                  <span>Kind</span>
                  <span>Status</span>
                  <span className="hidden md:block">Created</span>
                  <span className="hidden md:block" title="Latency from created to sent (queue → WS)">→ Sent</span>
                  <span className="hidden md:block" title="Latency from sent to acked (WS → device response)">→ Acked</span>
                  <span className="hidden md:block" title="Latency from acked to completed">→ Done</span>
                  <span>Detail</span>
                  <span />
                </div>
                {commands.map((cmd) => {
                  const created = cmd.created_at ? new Date(cmd.created_at).getTime() : null;
                  const sent = cmd.sent_at ? new Date(cmd.sent_at).getTime() : null;
                  const acked = cmd.acked_at ? new Date(cmd.acked_at).getTime() : null;
                  const completed = cmd.completed_at ? new Date(cmd.completed_at).getTime() : null;
                  const fmtDelta = (a: number | null, b: number | null) => {
                    if (a == null || b == null) return '—';
                    const ms = b - a;
                    if (ms < 0) return '—';
                    if (ms < 1000) return `${ms}ms`;
                    return `${(ms / 1000).toFixed(1)}s`;
                  };
                  const createdTime = created
                    ? new Date(created).toLocaleTimeString([], { hour12: false })
                    : '—';
                  // Detail summary by command kind. Surfaces the bits an
                  // operator actually cares about when diagnosing
                  // start/stop/geofence flows — revision for geofence_set,
                  // session_id for the recording commands, etc.
                  const detail = (() => {
                    if (cmd.kind === 'geofence_set') {
                      const rev = cmd.payload?.revision ?? cmd.result?.revision;
                      const gates = Array.isArray(cmd.payload?.gates)
                        ? cmd.payload.gates.length
                        : null;
                      const parts: string[] = [];
                      if (rev != null) parts.push(`rev ${rev}`);
                      if (gates != null) parts.push(`${gates} gates`);
                      return parts.join(' · ');
                    }
                    if (cmd.kind === 'phone.start_recording' || cmd.kind === 'phone.stop_recording') {
                      const sid = cmd.payload?.session_id || cmd.result?.session_id;
                      return sid ? String(sid).slice(0, 40) : '';
                    }
                    return '';
                  })();
                  return (
                    <div
                      key={cmd.uuid}
                      className="grid grid-cols-[1fr_80px_minmax(0,1.2fr)_36px] items-center gap-3 border-b border-border/40 px-2 py-1 text-xs md:grid-cols-[140px_90px_80px_70px_70px_70px_1fr_36px]"
                    >
                      <span className="truncate font-mono text-fg" title={cmd.kind}>
                        {cmd.kind}
                      </span>
                      <Badge
                        variant="outline"
                        className={cn(
                          'justify-center font-mono text-[10px]',
                          STATUS_BADGE_CLASS[cmd.status] || 'border-fg-muted text-fg-muted',
                        )}
                      >
                        {cmd.status}
                      </Badge>
                      <span className="hidden font-mono text-fg-secondary md:block">{createdTime}</span>
                      <span className="hidden font-mono text-fg-muted md:block">{fmtDelta(created, sent)}</span>
                      <span className="hidden font-mono text-fg-muted md:block">{fmtDelta(sent, acked)}</span>
                      <span className="hidden font-mono text-fg-muted md:block">{fmtDelta(acked, completed)}</span>
                      <span className="truncate text-fg-secondary">
                        {cmd.error ? (
                          <span className="font-mono text-status-error">{cmd.error}</span>
                        ) : (
                          detail
                        )}
                      </span>
                      {cmd.status === 'queued' ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => cancelCommand(cmd.uuid)}
                              className="h-6 w-6 text-fg-muted hover:bg-status-error/10 hover:text-status-error"
                              aria-label="Cancel queued command"
                            >
                              <XIcon className="size-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Cancel queued command</TooltipContent>
                        </Tooltip>
                      ) : (
                        <span />
                      )}
                    </div>
                  );
                })}
              </div>
              </div>
              </TooltipProvider>
            )}
          </CardContent>
        </Card>
      </div>

      <DeleteSessionDialog
        sessionId={deleteTarget?.session_id ?? null}
        segmentCount={deleteTarget?.segment_count}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async (sid) => {
          await api.deleteSession(sid);
          setSessions((prev) => prev.filter((x) => x.session_id !== sid));
        }}
      />

      {previewCamera && (
        <SnapshotDialog
          open={!!previewCamera}
          onClose={() => setPreviewCamera(null)}
          deviceId={device.device_id}
          cameraId={previewCamera.name || previewCamera.camera_id}
          cameraLabel={previewCamera.name || previewCamera.camera_id}
        />
      )}
      {device.current_session_id && (
        <ShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          deviceId={device.device_id}
          sessionId={device.current_session_id}
          label={`${device.name || device.device_id} · ${device.current_session_id}`}
        />
      )}
      {restartOpen && (
        <RestartConfirmDialog
          device={device}
          open={restartOpen}
          onClose={() => setRestartOpen(false)}
          onConfirm={async (kind) => {
            await onCommand(kind);
            setRestartOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default DeviceDetail;
